import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Camera, BookOpen, Users, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const WarResiduesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-amber-900 to-stone-800 text-white py-24">
        <div className="absolute inset-0 bg-black/50"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_content-upgrade-11/artifacts/dtcmgynk_image.jpg)'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              War Residues
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-amber-100">
              Objects, Memory, and the Legacy of WWII along the Naga–Myanmar Border
            </p>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">About the Project</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                Supported by the Delta on the Move Foundation, the <strong>War Residues</strong> project explores how memories of the Second World War continue to circulate 
                through everyday objects and oral histories in the highland borderlands of Nagaland and Myanmar. Often 
                described as a 'Forgotten War', WWII profoundly shaped Naga communities along the India–Myanmar borderlands, 
                yet their perspectives remain largely absent from dominant historical narratives.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                The project focuses on wartime objects—including aircraft debris, weapons, and tools—that local communities 
                repurposed for everyday use after the war. Together with oral histories preserved in local Naga languages, 
                these materials reveal forms of agency, creativity, and negotiation, as well as diverse and locally grounded 
                understandings of what the war meant—and continues to mean—to different communities.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Fieldwork will be conducted in border districts including Noklak, Meluri, and Kiphire, documenting 
                endangered oral histories and artefacts from a rapidly disappearing generation of war witnesses. The 
                project will also examine the emergence of village-based war museums and grassroots heritage initiatives, 
                situating them within broader debates on memory, nationalism, tourism, and globalization.
              </p>
              
              {/* HAB Partnership */}
              <div className="bg-amber-50 border-l-4 border-amber-600 p-6 my-8 rounded-r-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Partnership with Humanities Across Borders</h3>
                <p className="text-gray-700 leading-relaxed">
                  The Highland Institute began partnering with Humanities Across Borders (HAB) in December 2025 in connection 
                  with War Residues. The two organisations share an appreciation for community knowledge, making HAB a natural 
                  partner in this work. HAB has supported the project team with advice on documenting, interpreting, and 
                  presenting wartime objects, including through the use of HAB Accession Cards for selected materials.
                </p>
              </div>
              
              <p className="text-gray-700 leading-relaxed">
                Using an interdisciplinary and multimodal approach that combines ethnography, oral history, object-based 
                research, photography, illustration, and other creative methods, War Residues aims to reframe the history 
                of WWII from the perspectives of marginalized border communities. Planned outcomes include an illustrated 
                booklet, academic publications, and public workshops hosted by the Highland Institute, bringing the objects 
                and their associated stories to wider audiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Research Focus Areas */}
      <section className="py-16 bg-amber-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Research Focus</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <Camera className="w-6 h-6 text-amber-600" />
                </div>
                <CardTitle>Object-Based Research</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Documenting wartime objects repurposed for everyday use—aircraft debris, weapons, and tools
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-amber-600" />
                </div>
                <CardTitle>Oral Histories</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Recording endangered testimonies in local Naga languages from the last generation of war witnesses
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-amber-600" />
                </div>
                <CardTitle>Village Museums</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Examining grassroots heritage initiatives and village-based war museums along the border
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <BookOpen className="w-6 h-6 text-amber-600" />
                </div>
                <CardTitle>Memory & Identity</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Exploring debates on memory, nationalism, tourism, and globalization in border communities
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Fieldwork Locations */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Fieldwork Locations</h2>
            <div className="bg-stone-50 border-l-4 border-amber-600 p-6 rounded-r-lg mb-8">
              <p className="text-gray-700 leading-relaxed">
                Research will be conducted in the border districts of Eastern Nagaland along the Indo–Myanmar frontier:
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-center text-gray-700">
                  <MapPin className="w-4 h-4 text-amber-600 mr-2" />
                  <strong>Noklak District</strong>
                </li>
                <li className="flex items-center text-gray-700">
                  <MapPin className="w-4 h-4 text-amber-600 mr-2" />
                  <strong>Meluri District</strong>
                </li>
                <li className="flex items-center text-gray-700">
                  <MapPin className="w-4 h-4 text-amber-600 mr-2" />
                  <strong>Kiphire District</strong>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-16 bg-stone-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Methodology</h2>
            <p className="text-gray-700 mb-6">
              The project employs an interdisciplinary and multimodal approach:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-semibold text-gray-900 mb-2">Ethnography</h3>
                <p className="text-gray-600 text-sm">Community-embedded fieldwork and participant observation</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-semibold text-gray-900 mb-2">Oral History</h3>
                <p className="text-gray-600 text-sm">Recording testimonies in local Naga languages</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-semibold text-gray-900 mb-2">Object-Based Research</h3>
                <p className="text-gray-600 text-sm">Documentation and analysis of wartime artefacts</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-semibold text-gray-900 mb-2">Photography & Illustrations</h3>
                <p className="text-gray-600 text-sm">Visual documentation of objects, places, and people</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md md:col-span-2">
                <h3 className="font-semibold text-gray-900 mb-2">Other Creative Methods</h3>
                <p className="text-gray-600 text-sm">Innovative approaches to knowledge production and dissemination</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Planned Outcomes */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Planned Outcomes</h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <ArrowRight className="w-5 h-5 text-amber-600 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-700"><strong>Illustrated Booklet:</strong> A visual publication documenting war objects and community memories</span>
              </div>
              <div className="flex items-start">
                <ArrowRight className="w-5 h-5 text-amber-600 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-700"><strong>Academic Publications:</strong> Peer-reviewed articles and book chapters</span>
              </div>
              <div className="flex items-start">
                <ArrowRight className="w-5 h-5 text-amber-600 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-700"><strong>Public Workshops:</strong> Community events hosted by the Highland Institute</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsors */}
      <section className="py-16 bg-amber-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-10">Supported By</h2>
            
            {/* Primary Sponsor */}
            <div className="bg-white p-8 rounded-lg shadow-lg mb-8 border-l-4 border-amber-600">
              <p className="text-xl text-gray-800 font-medium">
                This project is funded by the <strong className="text-amber-700">Delta on the Move Foundation</strong>
              </p>
            </div>

            {/* Secondary Sponsor */}
            <div className="bg-white/60 p-6 rounded-lg">
              <p className="text-gray-600">
                Additionally, the <strong>Humanities Across Borders</strong> program at the International Institute for Asian Studies at Leiden University provides pedagogical training for the participating researchers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-amber-700 to-stone-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Get Involved</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Interested in learning more about this project or collaborating with our research team?
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="mailto:info@highlandinstitute.org">
              <Button className="bg-white text-amber-700 hover:bg-gray-100 px-8 py-6 text-lg">
                Contact Us
              </Button>
            </a>
            <Link to="/research">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Explore All Projects
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WarResiduesPage;
